// frontend/src/components/notifications/notificationUtils.js
// Comprehensive notification utility functions for event types and channels

import notificationService from '../../services/notificationService';
import { hasRoleLevel } from '../../utils/roleUtils';

const isTruthy = (value) => value === true || value === 1;
const DEFAULT_EVENT_ROLE = 'admin';
const DEFAULT_CHANNEL_ROLE = 'admin';

// ==================== NORMALIZATION HELPERS ====================

const normalizeEventType = (eventType) => {
  if (!eventType) {
    return null;
  }

  return {
    ...eventType,
    system_active: isTruthy(eventType.system_active),
    is_enabled: isTruthy(eventType.is_enabled),
    is_available: isTruthy(eventType.is_available),
    required_role: eventType.required_role || DEFAULT_EVENT_ROLE
  };
};

const normalizeChannel = (channel, globalChannelMeta = {}) => ({
  ...channel,
  channel: channel?.channel,
  globallyActive: isTruthy(globalChannelMeta.is_active ?? channel?.globallyAvailable),
  required_role: globalChannelMeta.required_role || DEFAULT_CHANNEL_ROLE,
  is_enabled: isTruthy(channel?.is_enabled ?? channel?.isEnabled),
  isEnabled: isTruthy(channel?.is_enabled ?? channel?.isEnabled),
  useCustomProvider: Boolean(channel?.useCustomProvider),
  hasPermission: false,
  canToggle: false
});

// ==================== EVENT TYPE UTILITIES ====================

export const isEventTypeGloballyActive = (visibilityData, eventKey) => {
  if (!visibilityData || !eventKey) {
    return false;
  }

  const eventTypes = visibilityData.eventTypes?.globalEventTypesWithRoles || [];
  const match = eventTypes.find(type => type.event_key === eventKey);
  return isTruthy(match?.system_active);
};

export const isEventTypeEnabledForEntity = (visibilityData, eventKey) => {
  if (!visibilityData || !eventKey) {
    return false;
  }

  const eventTypes = visibilityData.eventTypes?.globalEventTypesWithRoles || [];
  const match = eventTypes.find(type => type.event_key === eventKey);
  return isTruthy(match?.is_enabled);
};

export const isEventTypeAvailable = (visibilityData, eventKey) => (
  isEventTypeGloballyActive(visibilityData, eventKey) &&
  isEventTypeEnabledForEntity(visibilityData, eventKey)
);

export const getManageableEventTypes = (visibilityData, userRole) => {
  if (!visibilityData || !userRole) {
    return [];
  }

  const globalEventTypes = visibilityData.eventTypes?.globalEventTypesWithRoles || [];

  return globalEventTypes
    .map(normalizeEventType)
    .filter(eventType => {
      if (!eventType) return false;
      if (!eventType.system_active) return false;
      return hasRoleLevel(userRole, eventType.required_role);
    });
};

export const canManageNotificationEventTypes = (visibilityData, userRole) => (
  getManageableEventTypes(visibilityData, userRole).length > 0
);

// ==================== CHANNEL UTILITIES ====================

export const isChannelGloballyActive = (visibilityData, channelName) => {
  if (!visibilityData || !channelName) {
    return false;
  }

  const channelMeta = visibilityData.globalChannelsWithRoles?.[channelName];
  const fallback = visibilityData.globalChannels?.[channelName];
  return isTruthy(channelMeta?.is_active ?? fallback);
};

export const isChannelEnabledForEntity = (entityChannels = [], channelName) => {
  if (!entityChannels || !channelName) {
    return false;
  }

  const match = entityChannels.find(channel => channel.channel === channelName);
  return isTruthy(match?.is_enabled ?? match?.isEnabled);
};

export const isChannelAvailable = (visibilityData, channelName) => {
  if (!visibilityData || !channelName) {
    return false;
  }

  const channels = visibilityData.channels || {};
  return isTruthy(channels[channelName]);
};

export const getManageableChannels = (visibilityData, entityChannels = [], userRole) => {
  if (!visibilityData || !userRole) {
    return [];
  }

  const globalChannelsWithRoles = visibilityData.globalChannelsWithRoles || {};
  const entityChannelByName = entityChannels.reduce((acc, channel) => {
    acc[channel.channel] = channel;
    return acc;
  }, {});

  return Object.entries(globalChannelsWithRoles)
    .reduce((manageable, [channelName, meta]) => {
      const normalized = normalizeChannel(entityChannelByName[channelName] || { channel: channelName }, meta);
      const hasPermission = hasRoleLevel(userRole, normalized.required_role);
      const globallyActive = normalized.globallyActive;

      if (!globallyActive || !hasPermission) {
        return manageable;
      }

      manageable.push({
        ...normalized,
        hasPermission,
        canToggle: true
      });
      return manageable;
    }, []);
};

export const canManageNotificationChannels = (visibilityData, userRole) => (
  getManageableChannels(visibilityData, [], userRole).length > 0
);

// ==================== MANAGEMENT MENU VISIBILITY ====================

export const shouldShowNotificationManagementMenu = async (entityId, userRole) => {
  if (!entityId || !userRole) {
    return false;
  }

  try {
    const visibility = await notificationService.checkMenuVisibility(entityId);
    const canManageEventTypes = canManageNotificationEventTypes(visibility, userRole);
    const canManageChannels = canManageNotificationChannels(visibility, userRole);
    return canManageEventTypes && canManageChannels;
  } catch (error) {
    console.error('Error checking notification management visibility:', error);
    return false;
  }
};

// ==================== MENU VISIBILITY ====================

export const shouldShowNotificationMenu = async (entityId) => {
  if (!entityId) {
    return false;
  }

  try {
    const visibility = await notificationService.checkMenuVisibility(entityId);
    return visibility.visible === true;
  } catch (error) {
    console.error('Error checking notification menu visibility:', error);
    return false;
  }
};

export const getNotificationVisibilityDetails = async (entityId) => {
  if (!entityId) {
    return {
      visible: false,
      hasAvailableEventTypes: false,
      hasAvailableChannels: false,
      availableEventTypes: [],
      availableChannels: {},
      reason: 'no_entity_id'
    };
  }

  try {
    const visibility = await notificationService.checkMenuVisibility(entityId);
    const availableChannels = visibility.channels || {};
    const hasAvailableChannel = Object.values(availableChannels).some(isTruthy);
    const hasAvailableEventType = visibility.eventTypes?.hasAvailable || false;

    return {
      visible: visibility.visible,
      hasAvailableEventTypes: hasAvailableEventType,
      hasAvailableChannels: hasAvailableChannel,
      availableEventTypes: visibility.eventTypes?.keys || [],
      availableChannels,
      globalChannels: visibility.globalChannels || {},
      reason: visibility.reason
    };
  } catch (error) {
    console.error('Error getting notification visibility details:', error);
    return {
      visible: false,
      hasAvailableEventTypes: false,
      hasAvailableChannels: false,
      availableEventTypes: [],
      availableChannels: {},
      reason: 'error'
    };
  }
};

// ==================== ELIGIBILITY HELPERS ====================

export const getTemplateAndStatisticsEligibility = (visibilityData, entityChannels, userRole) => {
  const manageableEventTypes = getManageableEventTypes(visibilityData, userRole);
  const manageableChannels = getManageableChannels(visibilityData, entityChannels, userRole);
  const entityEnabledEventTypes = manageableEventTypes.filter(eventType => eventType?.is_enabled);
  const entityEnabledChannels = manageableChannels.filter(channel => channel?.is_enabled);

  return {
    manageableEventTypes,
    manageableChannels,
    enabledEventTypes: entityEnabledEventTypes,
    enabledChannels: entityEnabledChannels,
    hasEventTypeAccess: manageableEventTypes.length > 0,
    hasChannelAccess: manageableChannels.length > 0,
    hasTemplateAccess: entityEnabledEventTypes.length > 0 && entityEnabledChannels.length > 0
  };
};
